import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Fab,
  Drawer,
  Typography,
  TextField,
  IconButton,
  CircularProgress,
  Alert,
  LinearProgress,
  Tooltip,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import CloseIcon from '@mui/icons-material/Close';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { aiApi, ChatMessage, TokenStatus } from '../../services/aiApi';
import { useAppState } from '../../contexts/AppStateContext';

interface MessageBubble extends ChatMessage {
  id: number;
}

export const AiCompanion: React.FC = () => {
  const { ai_companion } = useAppState();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<MessageBubble[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [tokenStatus, setTokenStatus] = useState<TokenStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  let nextId = useRef(0);

  useEffect(() => {
    if (open && tokenStatus === null) {
      aiApi.getTokenStatus().then(setTokenStatus).catch(() => {});
    }
  }, [open, tokenStatus]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || loading) return;

    setInput('');
    setError(null);

    const userMsg: MessageBubble = { id: nextId.current++, role: 'user', content: text };
    setMessages(prev => [...prev, userMsg]);
    setLoading(true);

    const history: ChatMessage[] = messages.map(m => ({ role: m.role, content: m.content }));

    try {
      const res = await aiApi.sendMessage(text, history);
      const assistantMsg: MessageBubble = {
        id: nextId.current++,
        role: 'assistant',
        content: res.reply,
      };
      setMessages(prev => [...prev, assistantMsg]);
      setTokenStatus(res.tokens_remaining);
    } catch (err: any) {
      if (err?.response?.status === 429) {
        setError('本日のAI利用上限に達しました。明日またお試しください。');
      } else {
        setError('エラーが発生しました。しばらく経ってからお試しください。');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const tokenPercent = tokenStatus
    ? Math.round((tokenStatus.used / tokenStatus.limit) * 100)
    : 0;

  // admin(SiteOption)のキルスイッチ。無効時は何も描画しない（ボタン非表示）
  if (!ai_companion?.enabled) return null;

  return (
    <>
      <Fab
        onClick={() => setOpen(true)}
        sx={{
          position: 'fixed',
          bottom: { xs: 72, sm: 24 },
          right: 24,
          background: 'linear-gradient(135deg, #6C63FF 0%, #4ECDC4 100%)',
          color: '#fff',
          zIndex: 1200,
          '&:hover': {
            background: 'linear-gradient(135deg, #5A52E0 0%, #3DBDB5 100%)',
          },
        }}
      >
        <AutoAwesomeIcon />
      </Fab>

      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: '100vw', sm: 400 },
            display: 'flex',
            flexDirection: 'column',
            bgcolor: 'background.paper',
          },
        }}
      >
        {/* ヘッダー */}
        <Box
          sx={{
            p: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            background: 'linear-gradient(135deg, #6C63FF 0%, #4ECDC4 100%)',
            color: '#fff',
            flexShrink: 0,
          }}
        >
          <AutoAwesomeIcon fontSize="small" />
          <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1 }}>
            PULSE AI
          </Typography>
          <IconButton size="small" onClick={() => setOpen(false)} sx={{ color: '#fff' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* トークン残量バー */}
        {tokenStatus && (
          <Tooltip title={`本日の残り利用量: ${tokenStatus.remaining.toLocaleString()} / ${tokenStatus.limit.toLocaleString()} トークン`}>
            <Box sx={{ px: 2, pt: 1, flexShrink: 0 }}>
              <LinearProgress
                variant="determinate"
                value={Math.min(tokenPercent, 100)}
                sx={{
                  height: 3,
                  borderRadius: 2,
                  bgcolor: 'action.hover',
                  '& .MuiLinearProgress-bar': {
                    bgcolor: tokenPercent > 80 ? 'warning.main' : '#6C63FF',
                  },
                }}
              />
            </Box>
          </Tooltip>
        )}

        {/* メッセージエリア */}
        <Box sx={{ flex: 1, overflowY: 'auto', p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {messages.length === 0 && (
            <Box sx={{ textAlign: 'center', mt: 4, color: 'text.secondary' }}>
              <AutoAwesomeIcon sx={{ fontSize: 40, mb: 1, opacity: 0.4 }} />
              <Typography variant="body2">
                ゲームライブラリについて何でも聞いてください
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 0.5, opacity: 0.7 }}>
                例: 積みゲー何本ある？ / 次に何やる？ / 今週末クリアできるゲームは？
              </Typography>
            </Box>
          )}

          {messages.map(msg => (
            <Box
              key={msg.id}
              sx={{
                display: 'flex',
                justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
              }}
            >
              <Box
                sx={{
                  maxWidth: '85%',
                  px: 1.5,
                  py: 1,
                  borderRadius: msg.role === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                  bgcolor: msg.role === 'user' ? '#6C63FF' : 'action.hover',
                  color: msg.role === 'user' ? '#fff' : 'text.primary',
                  fontSize: '0.875rem',
                  lineHeight: 1.6,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {msg.content}
              </Box>
            </Box>
          ))}

          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
              <Box
                sx={{
                  px: 2,
                  py: 1.5,
                  borderRadius: '16px 16px 16px 4px',
                  bgcolor: 'action.hover',
                }}
              >
                <CircularProgress size={16} thickness={4} sx={{ color: '#6C63FF' }} />
              </Box>
            </Box>
          )}

          {error && (
            <Alert severity="warning" sx={{ fontSize: '0.8rem' }}>
              {error}
            </Alert>
          )}

          <div ref={bottomRef} />
        </Box>

        {/* 入力エリア */}
        <Box
          sx={{
            p: 2,
            borderTop: 1,
            borderColor: 'divider',
            display: 'flex',
            gap: 1,
            alignItems: 'flex-end',
            flexShrink: 0,
          }}
        >
          <TextField
            inputRef={inputRef}
            multiline
            maxRows={4}
            fullWidth
            size="small"
            placeholder="ゲームについて聞く..."
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={loading}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 3,
                fontSize: '0.875rem',
              },
            }}
          />
          <IconButton
            onClick={sendMessage}
            disabled={!input.trim() || loading}
            sx={{
              bgcolor: '#6C63FF',
              color: '#fff',
              flexShrink: 0,
              '&:hover': { bgcolor: '#5A52E0' },
              '&.Mui-disabled': { bgcolor: 'action.disabledBackground' },
            }}
          >
            <SendIcon fontSize="small" />
          </IconButton>
        </Box>
      </Drawer>
    </>
  );
};

export default AiCompanion;
